/**
 * Shared API fetch helper.
 *
 * Centralises three things every typed fetcher used to do by hand:
 *
 * 1. Attach the `Authorization: Bearer <token>` header.
 * 2. Map response statuses to a uniform `ApiError` instead of ad-hoc
 *    `throw new Error("forbidden")` strings.
 * 3. Run a 401 interceptor — when the backend says the caller is no
 *    longer authenticated (e.g., the MSAL access token has been silently
 *    revoked / drifted), let the app sign the user out instead of
 *    leaving the UI in a broken-on-every-call state.
 *
 * The interceptor is a module-level slot — set once at app bootstrap by
 * `MsalBootstrap`. Keeping it module-scoped (rather than threading it
 * through a context) means every fetcher in `shared/api/*.ts` can rely
 * on it without each call site having to wire it up.
 *
 * **Background requests** (`background: true`) carry `X-Daquma-Background: 1`.
 * Polling — the notification bell, a run's progress — is not a person using
 * the app, so the server does not count it towards the 10-minute idle limit
 * (ADR-0046). Anything a person clicked is sent without it.
 *
 * The shape of errors is the API's documented envelope:
 * `{ error: { code, message, ... } }`. We try to surface `error.code` as
 * `ApiError.code` so callers can branch on it; otherwise we fall back to
 * `http_<status>`.
 */

export interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody | string | null;

  constructor(status: number, code: string, message: string, body: ApiErrorBody | string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Receives the 401's error code (`session_ended`, `authentication_failed`…). */
type UnauthorisedHandler = (code: string) => void;

let unauthorisedHandler: UnauthorisedHandler | null = null;

/**
 * Install the global 401 interceptor. Call this once at app bootstrap.
 * Idempotent — passing `null` clears the handler (useful in tests).
 */
export function setUnauthorisedHandler(handler: UnauthorisedHandler | null): void {
  unauthorisedHandler = handler;
}

interface ApiFetchOptions extends Omit<RequestInit, "body" | "headers"> {
  /** Bearer token. Omit for the few endpoints that are anonymous. */
  token?: string | null;
  /** Plain object — will be JSON-serialised and Content-Type set. */
  json?: unknown;
  /** Pre-serialised body (string / FormData / etc). */
  body?: BodyInit;
  /** Extra headers. */
  headers?: Record<string, string>;
  /** Polling, not a person: excluded from the idle limit. */
  background?: boolean;
}

/** The header that marks a request as background (ADR-0046). */
export const BACKGROUND_HEADER = "X-Daquma-Background";

let lastRequestIdSeen: string | null = null;

/** The request id of the last thing a person did, for a support ticket's
 *  diagnostics (ADR-0093). Polling does not count: it says nothing of what they
 *  were doing. */
export function lastRequestId(): string | null {
  return lastRequestIdSeen;
}

async function readBody(response: Response): Promise<ApiErrorBody | string | null> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      return (await response.json()) as ApiErrorBody;
    } catch {
      return null;
    }
  }
  try {
    return await response.text();
  } catch {
    return null;
  }
}

function errorFrom(status: number, body: ApiErrorBody | string | null): ApiError {
  let code = `http_${status}`;
  let message = `HTTP ${status}`;
  if (body && typeof body === "object" && body.error) {
    if (body.error.code) code = body.error.code;
    if (body.error.message) message = body.error.message;
  } else if (typeof body === "string" && body.length > 0) {
    message = body;
  }
  return new ApiError(status, code, message, body);
}

/**
 * The workhorse. Returns the parsed JSON body for 2xx, or throws
 * `ApiError`. For 204 returns `undefined` cast to `T` (callers that
 * expect no body should pass `T = void`).
 */
export async function apiFetch<T>(url: string, opts: ApiFetchOptions = {}): Promise<T> {
  const { token, json, body: rawBody, headers: extra, background, ...init } = opts;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...extra,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (background) headers[BACKGROUND_HEADER] = "1";

  let body: BodyInit | undefined = rawBody;
  if (json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(json);
  }

  const response = await fetch(url, {
    ...init,
    headers,
    body,
  });

  if (!background) lastRequestIdSeen = response.headers?.get("x-request-id") ?? lastRequestIdSeen;

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const error = errorFrom(response.status, await readBody(response));
    if (response.status === 401 && unauthorisedHandler) {
      // Fire-and-forget — the handler typically signs out and navigates;
      // the caller's promise still rejects.
      try {
        unauthorisedHandler(error.code);
      } catch {
        // Don't let a buggy handler swallow the original 401.
      }
    }
    throw error;
  }

  // 2xx with body — assume JSON. Endpoints that return non-JSON should
  // not use this helper.
  return (await response.json()) as T;
}
